<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import InboxesAPI from 'dashboard/api/inboxes';
import router from '../../../index';
import PageHeader from '../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import PipelineStageAccordion from 'dashboard/components-next/Deals/PipelineStageAccordion.vue';

const { t } = useI18n();
const route = useRoute();
const pipelinesStore = usePipelinesStore();

const autoCreateDealEnabled = ref(false);
const pipelineStageId = ref(null);
const isSaving = ref(false);
const hasError = ref(false);

const allPipelines = computed(() => pipelinesStore.getPipelinesList);
const activePipelineId = computed(() => {
  if (!pipelineStageId.value) return null;
  const pipeline = allPipelines.value.find(p =>
    (p.stages || []).some(stage => stage.id === pipelineStageId.value)
  );
  return pipeline?.id ?? null;
});

onMounted(() => {
  pipelinesStore.get();
});

const goToFinish = () => {
  router.replace({
    name: 'settings_inbox_finish',
    params: { page: 'new', inbox_id: route.params.inbox_id },
  });
};

const handleSave = async () => {
  if (autoCreateDealEnabled.value && !pipelineStageId.value) {
    hasError.value = true;
    return;
  }
  hasError.value = false;
  isSaving.value = true;
  try {
    await InboxesAPI.update(route.params.inbox_id, {
      auto_create_deal_enabled: autoCreateDealEnabled.value,
      auto_create_deal_pipeline_stage_id: autoCreateDealEnabled.value
        ? pipelineStageId.value
        : null,
    });
    goToFinish();
  } catch (error) {
    useAlert(error.message);
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="handleSave">
      <div class="w-full">
        <PageHeader
          :header-title="t('INBOX_MGMT.ADD.AUTO_DEAL.TITLE')"
          :header-content="t('INBOX_MGMT.ADD.AUTO_DEAL.DESC')"
        />
      </div>
      <div class="w-full mb-4">
        <SettingsToggleSection
          v-model="autoCreateDealEnabled"
          :header="t('INBOX_MGMT.ADD.AUTO_DEAL.TOGGLE_LABEL')"
          :description="t('INBOX_MGMT.ADD.AUTO_DEAL.TOGGLE_HELP_TEXT')"
        >
          <template v-if="autoCreateDealEnabled" #editor>
            <label :class="{ error: hasError }">
              {{ t('INBOX_MGMT.ADD.AUTO_DEAL.PIPELINE_STAGE_LABEL') }}
              <PipelineStageAccordion
                v-model="pipelineStageId"
                :pipelines="allPipelines"
                :active-pipeline-id="activePipelineId"
                :has-error="hasError"
                :message="t('INBOX_MGMT.ADD.AUTO_DEAL.VALIDATION_ERROR')"
                :placeholder="
                  t('INBOX_MGMT.ADD.AUTO_DEAL.PIPELINE_STAGE_PLACEHOLDER')
                "
              />
            </label>
          </template>
        </SettingsToggleSection>
      </div>
      <div class="w-full">
        <NextButton
          type="submit"
          :is-loading="isSaving"
          solid
          blue
          :label="t('INBOX_MGMT.ADD.AUTO_DEAL.BUTTON_TEXT')"
        />
      </div>
    </form>
  </div>
</template>
